import { Controller } from "@hotwired/stimulus"

// Generic coordinate picker controller that can be reused for any form
// that needs to pick coordinates on PDF preview images
export default class extends Controller {
  static targets = [
    "previewPage",
    "coordinatesList",
    "coordinateInputs",
    "remainingCount"
  ]

  static values = {
    fieldNamePrefix: String, // e.g., "partner_document[fields_attributes]"
    picking: { type: Boolean, default: false },
    fieldNames: { type: Array, default: [] }
  }

  connect() {
    this.boundListener = this.handleClick.bind(this)
    this.coordinateCounter = new Date().getTime()
    this.currentFieldElement = null // Track which field we're picking for
  }

  disconnect() {
    this.finishPicking()
  }

  // Add a new empty field row
  addField() {
    const uniqueId = this.coordinateCounter++
    const container = this.hasCoordinateInputsTarget ? 
                     this.coordinateInputsTarget : 
                     document.getElementById("coordinates-container")

    if (!container) {
      console.error("Could not find coordinates container")
      return
    }

    const fieldHtml = this.createCoordinateFieldHtml(uniqueId, "", "", "1")
    const temp = document.createElement("div")
    temp.innerHTML = fieldHtml
    const newField = temp.firstElementChild
    container.appendChild(newField)
    
    newField.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  // Begin picking for a specific field
  beginPickingForField(event) {
    const button = event.currentTarget
    const fieldElement = button.closest('.coordinate-field')
    
    if (this.pickingValue) {
      this.finishPicking()
      return
    }

    this.pickingValue = true
    this.currentFieldElement = fieldElement

    // Update button state
    button.textContent = "Click PDF to Select"
    button.classList.add("bg-alert-yellow-600", "hover:bg-alert-yellow-700")
    button.classList.remove("bg-complementary-blue-500", "hover:bg-complementary-blue-600")

    // Highlight the field being edited
    fieldElement.classList.add("ring-2", "ring-complementary-blue-500", "bg-complementary-blue-50")

    // Show instructions
    if (this.hasRemainingCountTarget) {
      this.remainingCountTarget.textContent = "Click on the PDF preview to select the coordinate position for this field"
      this.remainingCountTarget.classList.add("text-complementary-blue-600", "font-medium")
    }

    // Add click listeners to all preview pages
    this.previewPageTargets.forEach(page => {
      page.addEventListener("click", this.boundListener)
      page.classList.add("ring-2", "ring-complementary-blue-500", "cursor-crosshair")
    })
  }

  finishPicking() {
    this.pickingValue = false

    // Reset all pick buttons
    if (this.currentFieldElement) {
      const button = this.currentFieldElement.querySelector('[data-action*="beginPickingForField"]')
      if (button) {
        button.textContent = "Pick Coordinates"
        button.classList.remove("bg-alert-yellow-600", "hover:bg-alert-yellow-700")
        button.classList.add("bg-complementary-blue-500", "hover:bg-complementary-blue-600")
      }
      this.currentFieldElement.classList.remove("ring-2", "ring-complementary-blue-500", "bg-complementary-blue-50")
    }
    
    this.currentFieldElement = null

    // Clear instructions
    if (this.hasRemainingCountTarget) {
      this.remainingCountTarget.textContent = ""
      this.remainingCountTarget.classList.remove("text-complementary-blue-600", "font-medium")
    }

    // Remove click listeners from all preview pages
    this.previewPageTargets.forEach(page => {
      page.removeEventListener("click", this.boundListener)
      page.classList.remove("ring-2", "ring-complementary-blue-500", "cursor-crosshair")
    })
  }

  handleClick(event) {
    if (!this.pickingValue || !this.currentFieldElement) return

    // Get the coordinates
    const x = event.offsetX
    const y = event.offsetY
    const pageNumber = event.target.dataset.pageNumber

    // Convert pixel coordinates to PDF points
    const xPercent = x / event.target.width
    const yPercent = y / event.target.height

    // Detect portrait vs landscape orientation
    let xConversionFactor, yConversionFactor
    if (event.target.height > event.target.width) {
      xConversionFactor = 612
      yConversionFactor = 792
    } else {
      xConversionFactor = 792
      yConversionFactor = 612
    }

    const xPts = xPercent * xConversionFactor
    const yPts = yConversionFactor - (yPercent * yConversionFactor)

    // Add visual marker at click position
    this.addVisualMarker(event.target, x, y, pageNumber)

    // Update the coordinate inputs for the current field
    this.updateFieldCoordinates(xPts, yPts, pageNumber)

    // Show success feedback
    if (this.hasRemainingCountTarget) {
      this.remainingCountTarget.textContent = `Coordinate set to (${Math.round(xPts)}, ${Math.round(yPts)}) on page ${pageNumber}`
      this.remainingCountTarget.classList.add("text-alert-green-600")
      setTimeout(() => {
        this.remainingCountTarget.classList.remove("text-alert-green-600")
      }, 2000)
    }

    // Automatically finish picking after selecting coordinates
    setTimeout(() => {
      this.finishPicking()
    }, 500)
  }

  updateFieldCoordinates(x, y, pageNumber) {
    if (!this.currentFieldElement) return

    // Update the coordinate inputs in the current field
    const pageInput = this.currentFieldElement.querySelector('input[name*="[page]"]')
    const xInput = this.currentFieldElement.querySelector('input[name*="[x]"]')
    const yInput = this.currentFieldElement.querySelector('input[name*="[y]"]')

    if (pageInput) pageInput.value = pageNumber
    if (xInput) xInput.value = x.toFixed(1)
    if (yInput) yInput.value = y.toFixed(1)
  }

  addVisualMarker(imageElement, x, y, pageNumber) {
    // Remove any existing markers for this field across all pages
    if (this.currentFieldElement) {
      const fieldId = this.currentFieldElement.dataset.fieldId
      const existingMarkers = document.querySelectorAll(`[data-marker-field-id="${fieldId}"]`)
      existingMarkers.forEach(marker => marker.remove())
    }

    // Create a marker element to show where the user clicked
    const marker = document.createElement("div")
    marker.className = "absolute w-3 h-3 bg-alert-green-500 rounded-full border-2 border-white transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10"
    marker.style.left = `${x}px`
    marker.style.top = `${y}px`
    
    // Associate marker with the current field
    if (this.currentFieldElement) {
      marker.dataset.markerFieldId = this.currentFieldElement.dataset.fieldId
    }

    // Add marker to the parent container
    const container = imageElement.parentElement
    if (container.style.position !== "relative" && container.style.position !== "absolute") {
      container.style.position = "relative"
    }
    container.appendChild(marker)
  }

  createCoordinateFieldHtml(uniqueId, x, y, pageNumber) {
    const fieldNameOptions = this.getFieldNameOptions()
    const prefix = this.fieldNamePrefixValue || "partner_document[field_coordinates_attributes]"
    const xValue = x ? x.toFixed(1) : ""
    const yValue = y ? y.toFixed(1) : ""

    return `
      <div class="border border-support-light-300 rounded p-4 mb-3 coordinate-field" data-field-id="${uniqueId}">
        <div class="grid grid-cols-6 gap-3">
          <div class="col-span-2">
            <label class="block text-sm font-medium text-support-dark-700">Field</label>
            <select name="${prefix}[${uniqueId}][field_name]" required class="w-full rounded-md border-support-light-300 text-sm">
              <option value="">Select field...</option>
              ${fieldNameOptions}
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-support-dark-700">Page</label>
            <input type="number" name="${prefix}[${uniqueId}][page]" min="1" value="${pageNumber}" required class="w-full rounded-md border-support-light-300 text-sm">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-support-dark-700">X Position</label>
            <input type="number" name="${prefix}[${uniqueId}][x]" step="0.1" value="${xValue}" required class="w-full rounded-md border-support-light-300 text-sm">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-support-dark-700">Y Position</label>
            <input type="number" name="${prefix}[${uniqueId}][y]" step="0.1" value="${yValue}" required class="w-full rounded-md border-support-light-300 text-sm">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-support-dark-700">Font size</label>
            <input type="number" name="${prefix}[${uniqueId}][font_size]" value="12" min="6" max="72" required class="w-full rounded-md border-support-light-300 text-sm">
          </div>
        </div>
        
        <div class="grid grid-cols-4 gap-3 mt-3">
          <div>
            <label class="block text-sm font-medium text-support-dark-700">Font family</label>
            <select name="${prefix}[${uniqueId}][font_family]" class="w-full rounded-md border-support-light-300 text-sm">
              <option value="Helvetica">Helvetica</option>
              <option value="Times-Roman">Times-Roman</option>
              <option value="Courier">Courier</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-support-dark-700">Max Width (optional)</label>
            <input type="number" name="${prefix}[${uniqueId}][max_width]" class="w-full rounded-md border-support-light-300 text-sm">
          </div>
          
          <div class="flex items-end justify-end gap-2 col-span-2">
            <button type="button" 
                    data-action="click->coordinate-picker#beginPickingForField"
                    class="bg-complementary-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-complementary-blue-600">
              Pick Coordinates
            </button>
            <button type="button" onclick="this.closest('.coordinate-field').remove();" class="bg-alert-red-600 text-white px-3 py-1 rounded text-sm hover:bg-alert-red-700">
              Delete
            </button>
          </div>
        </div>
      </div>
    `
  }

  getFieldNameOptions() {
    // Use field names from the data attribute if available, otherwise use defaults
    const fieldNames = this.fieldNamesValue.length > 0 ? this.fieldNamesValue : [
      "member_name",
      "member_first_name",
      "member_last_name",
      "member_dob",
      "member_address",
      "member_city",
      "member_state",
      "member_zip",
      "partner_name",
      "location_name",
      "today_date"
    ]

    return fieldNames.map(name => `<option value="${name}">${name.replace(/_/g, " ")}</option>`).join("")
  }
}
